import { useMemo } from 'react';
import { FilterOption } from '@elementary/common-ui/components';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import {
  AssetField,
  TestExecutionField,
} from '@elementary/common-ui/services/gql/types';
import {
  getLineageAssetsFilterOptionsQuery,
  getLineageTestFilterOptionsQuery,
  type LineageAssetsFilterOptionsResponse,
  type LineageTestFilterOptionsResponse,
} from './api';

// Maps GraphQL fields to the lineage filter `property` keys the worker applies.
const ASSET_FIELD_TO_PROPERTY: Partial<Record<AssetField, string>> = {
  OWNER: 'owner',
  TAG: 'tag',
  DATABASE_NAME: 'databaseName',
  SCHEMA_NAME: 'schemaName',
  TABLE_NAME: 'tableName',
  BUILD_STRATEGY: 'materialization',
  PATH: 'path',
  ARTIFACT_TYPE: 'nodeType',
};

const TEST_FIELD_TO_PROPERTY: Partial<Record<TestExecutionField, string>> = {
  TEST_NAME: 'test',
};

const STATUS_OPTIONS = [
  { value: 'passed', label: 'Passed' },
  { value: 'warnings', label: 'Warnings' },
  { value: 'errors', label: 'Failures' },
];

// SaaS lineage filter options, sourced from GraphQL (replacing the report's
// models / groups / test_results).
// TODO: The `job` and `biNodeType` filters are not wired yet.
export const useLineageFilterOptions = (): FilterOption[] => {
  const envid = useSelectedEnv();
  const hasPermission = useEnvPermission();
  const canReadTests = hasPermission('VIEW_TESTS');

  const { data: assetFilterOptionsData } =
    useGraphqlQuery<LineageAssetsFilterOptionsResponse>({
      query: getLineageAssetsFilterOptionsQuery,
      queryKey: ['lineage-asset-filter-options', envid],
      enabled: !!envid,
      envId: envid,
    });

  const { data: testFilterOptionsData } =
    useGraphqlQuery<LineageTestFilterOptionsResponse>({
      query: getLineageTestFilterOptionsQuery,
      queryKey: ['lineage-test-filter-options', envid],
      enabled: !!envid && canReadTests,
      envId: envid,
    });

  return useMemo(() => {
    const assetOptions = parseGraphqlFilterOptions<AssetField>(
      assetFilterOptionsData?.assetsFilterOptions || [],
      ASSET_FIELD_TO_PROPERTY,
    );
    const testOptions = parseGraphqlFilterOptions<TestExecutionField>(
      testFilterOptionsData?.latestTestExecutionsFilterOptions || [],
      TEST_FIELD_TO_PROPERTY,
    );

    const filterOptions: FilterOption[] = [];

    if (canReadTests) {
      filterOptions.push(
        {
          property: 'status',
          propertyLabel: 'Test status',
          options: STATUS_OPTIONS,
          multiselect: true,
        },
        {
          property: 'test',
          propertyLabel: 'Test',
          options: testOptions.test || [],
          multiselect: true,
        },
      );
    }

    filterOptions.push(
      {
        property: 'owner',
        propertyLabel: 'Owner',
        options: assetOptions.owner || [],
        multiselect: true,
      },
      {
        property: 'tag',
        propertyLabel: 'Tag',
        options: assetOptions.tag || [],
        multiselect: true,
      },
      {
        property: 'databaseName',
        propertyLabel: 'Database name',
        options: assetOptions.databaseName || [],
        multiselect: true,
      },
      {
        property: 'schemaName',
        propertyLabel: 'Schema name',
        options: assetOptions.schemaName || [],
        multiselect: true,
      },
      {
        property: 'tableName',
        propertyLabel: 'Table name',
        options: assetOptions.tableName || [],
        multiselect: true,
      },
      {
        property: 'materialization',
        propertyLabel: 'Materialization',
        options: assetOptions.materialization || [],
        multiselect: true,
      },
      {
        property: 'path',
        propertyLabel: 'Path',
        options: assetOptions.path || [],
        multiselect: true,
      },
      {
        property: 'nodeType',
        propertyLabel: 'dbt node type',
        options: assetOptions.nodeType || [],
        multiselect: true,
      },
    );

    return filterOptions;
  }, [assetFilterOptionsData, testFilterOptionsData, canReadTests]);
};
